import React, { useState, useEffect } from 'react';
import { MdPerson, MdEmail, MdAccountBalance, MdLocationOn, MdArrowBack, MdFavorite, MdFavoriteBorder, MdFlag, MdClose, MdCheck } from 'react-icons/md';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from '../../config/api';
import { API_URL } from '../../config/api';
import { toast } from 'sonner';
import VerifiedBadge from '../../components/VerifiedBadge';

function getMatchBgGradient(percentage) {
  if (percentage >= 75) return 'from-green-500 to-green-600';
  if (percentage >= 50) return 'from-yellow-500 to-yellow-600';
  if (percentage >= 25) return 'from-orange-500 to-orange-600';
  return 'from-red-500 to-red-600';
}

const REPORT_REASONS = [
  { id: 'fake', label: 'Fake profile / Impersonation' },
  { id: 'inappropriate', label: 'Inappropriate content' },
  { id: 'misleading', label: 'Misleading information' },
  { id: 'not_candidate', label: 'Not a real candidate' },
  { id: 'other', label: 'Other' },
];

function RepProfileCard({ repProfile, isOwnProfile }) {
  const navigate = useNavigate();
  const { profile, match } = repProfile;
  const [isFavorited, setIsFavorited] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [reportError, setReportError] = useState('');

  useEffect(() => {
    if (profile.profile_id && !isOwnProfile) {
      axios.post(`${API_URL}/api/favorites/check`, { item_type: 'representative', item_ids: [profile.profile_id] })
        .then(res => setIsFavorited(!!res.data.favorited[profile.profile_id]))
        .catch(() => {});
    }
  }, [profile.profile_id, isOwnProfile]);

  const toggleFavorite = async () => {
    if (favLoading) return;
    setFavLoading(true);
    try {
      if (isFavorited) {
        await axios.delete(`${API_URL}/api/favorites`, { data: { item_type: 'representative', item_id: profile.profile_id } });
        setIsFavorited(false);
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API_URL}/api/favorites`, { item_type: 'representative', item_id: profile.profile_id });
        setIsFavorited(true);
        toast.success('Added to favorites');
      }
    } catch (err) {
      toast.error('Failed to update favorite');
    } finally {
      setFavLoading(false);
    }
  };

  const handleReport = async () => {
    if (!selectedReason) { setReportError('Please select a reason'); return; }
    setReportSubmitting(true);
    setReportError('');
    try {
      const reasonLabel = REPORT_REASONS.find(r => r.id === selectedReason)?.label || selectedReason;
      await axios.post(`${API_URL}/api/representatives/${profile.profile_id}/flag`, { reason: reasonLabel });
      setReportSubmitted(true);
      setTimeout(() => { setReportOpen(false); setReportSubmitted(false); setSelectedReason(''); }, 2000);
    } catch (err) {
      setReportError(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setReportSubmitting(false);
    }
  };

  const closeReport = () => { setReportOpen(false); setSelectedReason(''); setReportError(''); setReportSubmitted(false); };

  return (
    <>
      {/* Back to Reps button */}
      <button
        onClick={() => navigate('/reps')}
        className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-primary hover:text-primary shadow-sm transition-all"
        data-testid="back-to-reps-btn"
      >
        <MdArrowBack className="text-lg" />
        Back to Reps
      </button>

      {/* Profile Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header with match */}
        <div className={`bg-gradient-to-r ${isOwnProfile ? 'from-primary to-primary/80' : getMatchBgGradient(match.percentage)} p-6`}>
          <div className="flex items-center gap-6">
            {/* Profile Picture */}
            <div className="w-36 aspect-square rounded-xl bg-white/20 flex items-center justify-center overflow-hidden flex-shrink-0 border-4 border-white/30 shadow-lg">
              {profile.profile_picture_url ? (
                <img
                  src={profile.profile_picture_url}
                  alt={`${profile.first_name} ${profile.last_name}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <MdPerson className="text-6xl text-white/70" />
              )}
            </div>

            <div className="flex-grow text-white">
              <h2 className="text-3xl font-bold font-rubik flex items-center gap-2">
                {profile.first_name} {profile.last_name}
                <VerifiedBadge status={profile.verification_status} size="lg" />
              </h2>
              {profile.office && (
                <p className="text-white/90 flex items-center gap-2 mt-2 text-lg">
                  <MdAccountBalance className="text-xl" />
                  {profile.office.position_title}
                  {profile.office.level && (
                    <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{profile.office.level}</span>
                  )}
                </p>
              )}
              {(profile.running_location || profile.running_state) && (
                <p className="text-white/80 flex items-center gap-2 mt-1 text-base">
                  <MdLocationOn className="text-xl" />
                  {profile.running_location || profile.running_state}
                </p>
              )}
            </div>

            {/* Match Percentage + Favorite + Report */}
            {!isOwnProfile && (
              <div className="flex flex-col items-center gap-2">
                <div className="text-center bg-white/20 rounded-xl px-6 py-4">
                  <div className="text-4xl font-bold text-white">{match.percentage}%</div>
                  <div className="text-white/80 text-sm">Match</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleFavorite}
                    disabled={favLoading}
                    className="bg-white/20 hover:bg-white/30 rounded-xl p-2.5 transition-colors"
                    data-testid="fav-rep-btn"
                    title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    {isFavorited
                      ? <MdFavorite className="text-xl text-red-400" />
                      : <MdFavoriteBorder className="text-xl text-white/80" />
                    }
                  </button>
                  <button
                    onClick={() => setReportOpen(true)}
                    className="bg-white/20 hover:bg-red-500/40 rounded-xl p-2.5 transition-colors"
                    data-testid="report-rep-button"
                    title="Report Profile"
                  >
                    <MdFlag className="text-xl text-white/80" />
                  </button>
                </div>
              </div>
            )}
            {isOwnProfile && (
              <div className="text-center bg-white/20 rounded-xl px-6 py-4">
                <div className="text-sm font-medium text-white/90">Your Profile</div>
              </div>
            )}
          </div>
        </div>

        {/* Profile Details */}
        <div className="p-6">
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-2 text-gray-600">
              <MdEmail className="text-lg" />
              <a href={`mailto:${profile.primary_email}`} className="hover:text-primary">
                {profile.primary_email}
              </a>
            </div>
            {profile.secondary_email && (
              <div className="flex items-center gap-2 text-gray-600">
                <MdEmail className="text-lg" />
                <a href={`mailto:${profile.secondary_email}`} className="hover:text-primary">
                  {profile.secondary_email}
                </a>
              </div>
            )}
          </div>

          {profile.biography && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Biography</h3>
              <p className="text-gray-600 whitespace-pre-wrap">{profile.biography}</p>
            </div>
          )}

          {/* Poll Completion Progress */}
          {match.total_system_polls > 0 && (
            <div className="mb-6 bg-gray-50 rounded-lg p-4" data-testid="poll-completion-progress">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700">Poll Completion</h3>
                <span className="text-sm font-medium text-gray-600">
                  {match.rep_polls_answered} / {match.total_system_polls} polls answered
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="h-2.5 rounded-full transition-all duration-500 bg-primary"
                  style={{ width: `${Math.round((match.rep_polls_answered / match.total_system_polls) * 100)}%` }}
                  data-testid="poll-completion-bar"
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Match percentage is based on polls both you and {profile.first_name} have answered.
                {match.rep_polls_answered < match.total_system_polls && (
                  <span> More polls answered means a more accurate match.</span>
                )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Report Modal */}
      {reportOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeReport}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="report-rep-modal">
            {reportSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <MdCheck className="text-white text-3xl" />
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2 font-rubik">Thank You!</h2>
                <p className="text-gray-600 font-rubik">Your report has been submitted for admin review.</p>
              </div>
            ) : (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-800 font-rubik flex items-center gap-2">
                    <MdFlag className="text-red-500" />
                    Report this Profile
                  </h2>
                  <button onClick={closeReport} className="text-gray-400 hover:text-gray-600 transition-colors" data-testid="close-report-rep-modal">
                    <MdClose className="text-2xl" />
                  </button>
                </div>
                <p className="text-sm text-gray-600 font-rubik mb-4">
                  Why are you reporting {profile.first_name} {profile.last_name}'s profile?
                </p>
                <div className="space-y-3 mb-6">
                  {REPORT_REASONS.map(reason => (
                    <label
                      key={reason.id}
                      className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedReason === reason.id ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`report-rep-reason-${reason.id}`}
                    >
                      <input type="radio" name="reportReason" value={reason.id} checked={selectedReason === reason.id} onChange={e => setSelectedReason(e.target.value)} className="w-4 h-4 text-red-500 focus:ring-red-500" />
                      <span className="ml-3 text-sm font-medium text-gray-700 font-rubik">{reason.label}</span>
                    </label>
                  ))}
                </div>
                {reportError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600 font-rubik">{reportError}</p>
                  </div>
                )}
                <button
                  onClick={handleReport}
                  disabled={reportSubmitting || !selectedReason}
                  className="w-full py-3 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-report-rep-button"
                >
                  {reportSubmitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default RepProfileCard;
